/**
 * ExportChangeBadge
 *
 * Renders a one-line summary of what's changed since the last successful
 * export, with an expand toggle that shows the per-slide and audio diffs.
 * Drops cleanly into the publish-button dropdown above the export actions.
 *
 * Renders nothing when:
 *   - No prior export exists (project hasn't been exported yet)
 *   - The diff is clean (nothing changed since last export)
 *
 * The two cases are visually distinct upstream — see VideoPublishButton.
 */
import React, { useState } from 'react';
import { ChevronDown, AlertTriangle, FileText, Music, Volume2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ExportDiff } from '@/services/api';

interface ExportChangeBadgeProps {
  diff: ExportDiff;
  className?: string;
}

export const ExportChangeBadge: React.FC<ExportChangeBadgeProps> = ({ diff, className }) => {
  const [expanded, setExpanded] = useState(false);

  if (!diff.hasLastExport || diff.summary.isClean) return null;

  const { slides, backgroundMusic, narratorVolume } = diff.changes;
  const summaryParts: string[] = [];
  if (diff.summary.slidesChanged > 0) {
    summaryParts.push(
      `${diff.summary.slidesChanged} slide${diff.summary.slidesChanged === 1 ? '' : 's'}`
    );
  }
  if (backgroundMusic.changed) summaryParts.push('background music');
  if (narratorVolume.changed) summaryParts.push('narrator volume');
  const summaryText = summaryParts.join(' · ');

  const pct = (v: number | null | undefined): string =>
    typeof v === 'number' ? `${Math.round(v * 100)}%` : '—';

  const trackName = (m: { url: string; volume: number } | null): string => {
    if (!m) return 'none';
    const last = m.url.split('/').pop()?.split('?')[0];
    return last ? decodeURIComponent(last) : 'custom';
  };

  return (
    <div className={cn('px-2 py-1.5 bg-amber-500/10 border-b border-amber-500/20', className)}>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setExpanded(!expanded);
        }}
        className="flex items-center gap-1.5 w-full text-left"
      >
        <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
        <span className="text-xs text-amber-700 dark:text-amber-300 flex-1 min-w-0 truncate">
          Updated since last export: {summaryText}
        </span>
        <ChevronDown
          className={cn(
            'h-3 w-3 text-amber-600 transition-transform shrink-0',
            expanded && 'rotate-180'
          )}
        />
      </button>

      {expanded && (
        <div className="mt-2 space-y-1.5">
          {slides.length > 0 && (
            <div>
              <div className="flex items-center gap-1 text-[10px] font-medium text-amber-700 dark:text-amber-300 uppercase tracking-wide mb-0.5">
                <FileText className="h-3 w-3" /> Slides
              </div>
              <ul className="space-y-0.5 pl-4">
                {slides.map((s) => (
                  <li
                    key={s.outlineSlideId}
                    className="text-[11px] text-amber-800 dark:text-amber-200"
                  >
                    <span className="font-medium">{s.title || s.slideIdentifier}</span>
                    <span className="text-amber-600 dark:text-amber-400">
                      {' '}— v{s.oldVersion ?? '?'} → v{s.newVersion}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {backgroundMusic.changed && (
            <div>
              <div className="flex items-center gap-1 text-[10px] font-medium text-amber-700 dark:text-amber-300 uppercase tracking-wide mb-0.5">
                <Music className="h-3 w-3" /> Background music
              </div>
              <ul className="space-y-0.5 pl-4 text-[11px] text-amber-800 dark:text-amber-200">
                {backgroundMusic.old?.url !== backgroundMusic.new?.url && (
                  <li>
                    Track: {trackName(backgroundMusic.old)} → {trackName(backgroundMusic.new)}
                  </li>
                )}
                {backgroundMusic.old?.volume !== backgroundMusic.new?.volume && (
                  <li>
                    Volume: {pct(backgroundMusic.old?.volume ?? null)} →{' '}
                    {pct(backgroundMusic.new?.volume ?? null)}
                  </li>
                )}
              </ul>
            </div>
          )}

          {narratorVolume.changed && (
            <div>
              <div className="flex items-center gap-1 text-[10px] font-medium text-amber-700 dark:text-amber-300 uppercase tracking-wide mb-0.5">
                <Volume2 className="h-3 w-3" /> Narrator volume
              </div>
              <p className="pl-4 text-[11px] text-amber-800 dark:text-amber-200">
                {pct(narratorVolume.old)} → {pct(narratorVolume.new)}
              </p>
            </div>
          )}

          {diff.lastExportAt && (
            <p className="text-[10px] text-amber-600 dark:text-amber-400 pt-1 italic">
              Last exported {new Date(diff.lastExportAt).toLocaleString()}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
